import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { useStoreStatus } from "../lib/storeStatus";
import { Search, CheckCircle, ArrowRight, RotateCcw, Sparkles } from "lucide-react";

/**
 * Apex Finder — compact homepage recommender.
 *
 * Customer enters approximate Apex amount → server returns ONE best-match
 * option (product+qty OR eligible special offer). One button hands off to
 * the existing preconfigured checkout flow for products, or the existing
 * special-offer purchase flow for offers. Server-side revalidation of
 * eligibility, price and limits is done by those existing endpoints — this
 * component never authorises payment on its own.
 */
export default function ApexFinder() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { format, isBase } = useCurrency();
  const { salesPaused } = useStoreStatus();
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [going, setGoing] = useState(false);
  const analyticsSent = useRef(false);

  const parsedAmount = () => {
    const raw = String(input).replace(/[^\d]/g, "");
    const v = parseInt(raw, 10);
    return Number.isFinite(v) ? v : 0;
  };

  const onOpened = () => {
    if (analyticsSent.current) return;
    analyticsSent.current = true;
    api.post("/apex-finder/event", { event: "opened" }).catch(() => {});
  };

  const onInput = (e) => {
    onOpened();
    // Keep raw input separate from parsed number so the user can type freely,
    // but strip non-digits and format thousands on the fly for readability.
    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 8);
    if (!digits) { setInput(""); return; }
    const n = parseInt(digits, 10);
    setInput(n.toLocaleString());
  };

  const find = async () => {
    const amount = parsedAmount();
    if (!amount || amount < 100) {
      toast.error("Enter at least 100 Apex");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/apex-finder/recommend", { requested_apex: amount });
      setResult(data);
      api.post("/apex-finder/event", {
        event: "recommended",
        requested_apex: amount,
        recommendation_kind: data.recommendation.kind,
        recommendation_id: data.recommendation.product_id || data.recommendation.offer_id,
        recommended_apex: data.recommendation.apex_amount,
        recommended_price_gbp: data.recommendation.price_gbp,
      }).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong — try again");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setInput("");
  };

  const trackClick = () => {
    if (!result) return;
    api.post("/apex-finder/event", {
      event: "clicked_through",
      requested_apex: result.requested_apex,
      recommendation_kind: result.recommendation.kind,
      recommendation_id: result.recommendation.product_id || result.recommendation.offer_id,
      recommended_apex: result.recommendation.apex_amount,
      recommended_price_gbp: result.recommendation.price_gbp,
    }).catch(() => {});
  };

  const takeMeThere = async () => {
    if (!result) return;
    trackClick();
    const rec = result.recommendation;
    if (rec.kind === "product") {
      nav(rec.deep_link);
      return;
    }
    // Offer path — route to the dedicated pre-Stripe review page so the
    // customer can pick a game profile and accept the legal acknowledgements
    // before Stripe hand-off. The review page re-validates eligibility.
    if (!user) {
      toast.info("Sign in to claim this offer");
      nav(`/auth?mode=login&next=${encodeURIComponent(`/offer-checkout/${rec.offer_id}`)}`);
      return;
    }
    nav(`/offer-checkout/${rec.offer_id}`);
  };

  return (
    <section className="px-4 sm:px-8 max-w-7xl mx-auto mt-2 mb-8" data-testid="apex-finder">
      <div className="vault-card p-4 sm:p-6 relative overflow-hidden max-w-3xl mx-auto border-[var(--av-purple)]/40">
        {/* Subtle radial accent behind the card */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.08]"
             style={{ background: "radial-gradient(circle at top right, #9400D3 0%, transparent 60%)" }} />
        <div className="relative">
          <div className="mono-caption purple-text mb-2 flex items-center gap-2">
            <Search className="w-3.5 h-3.5" /> Apex Finder
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold leading-tight mb-2">
            HOW MUCH <span className="gold-text">APEX</span> DO YOU NEED?
          </h2>
          <p className="text-sm text-[var(--av-text-dim)] mb-4 max-w-lg">
            Enter the amount you're looking for and we'll find the best option for you.
          </p>

          {!result ? (
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              <div className="flex-1 min-w-0 relative">
                <input
                  data-testid="apex-finder-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9,]*"
                  autoComplete="off"
                  placeholder="e.g. 10,000"
                  aria-label="Requested Apex amount"
                  value={input}
                  onChange={onInput}
                  onFocus={onOpened}
                  onKeyDown={(e) => { if (e.key === "Enter") find(); }}
                  className="w-full h-14 sm:h-16 rounded-xl bg-[rgba(255,255,255,0.03)] border-2 border-[var(--av-border-strong)] focus:border-[var(--av-gold)] outline-none text-2xl sm:text-3xl font-serif font-bold text-center gold-text px-3 transition-colors"
                />
                <div className="mono-caption absolute right-3 top-1/2 -translate-y-1/2 text-[var(--av-text-muted)] pointer-events-none hidden sm:block">
                  Apex Coins
                </div>
              </div>
              <button
                data-testid="apex-finder-submit"
                onClick={find}
                disabled={loading}
                className="btn-gold shimmer sm:w-auto sm:min-w-[220px] h-14 sm:h-16 text-sm sm:text-base disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? "FINDING…" : "FIND MY BEST VALUE"}
              </button>
            </div>
          ) : (
            <div
              data-testid="apex-finder-result"
              className="rounded-xl border-2 border-[var(--av-gold)] bg-[rgba(212,175,55,0.06)] p-4 sm:p-5"
            >
              <div className="mono-caption gold-text mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> BEST VALUE FOUND
              </div>
              {/* Headline: Apex amount */}
              <div
                className="font-serif text-2xl sm:text-3xl font-bold leading-tight gold-text mb-2"
                data-testid="apex-finder-result-apex"
              >
                {result.recommendation.apex_amount.toLocaleString()} Apex
              </div>
              {/* Price row: main price on the left, 5% rewards callout on the right */}
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
                <div>
                  <div className="mono-caption text-[var(--av-text-dim)] mb-0.5">Price</div>
                  <div className="font-serif text-2xl sm:text-3xl font-bold leading-none"
                       data-testid="apex-finder-result-price">
                    {format(result.recommendation.price_gbp)}
                  </div>
                  {!isBase && (
                    <div className="text-[10px] text-[var(--av-text-muted)] mt-1">
                      charged £{Number(result.recommendation.price_gbp || 0).toFixed(2)} GBP
                    </div>
                  )}
                </div>
                {/* 5% Apex Rewards note — only for products (offers cannot
                    redeem rewards, so showing a discount there would mislead). */}
                {result.recommendation.kind === "product" ? (
                  <div className="rounded-lg border border-[var(--av-purple)]/60 bg-[rgba(148,0,211,0.10)] px-3 py-2 sm:text-right"
                       data-testid="apex-finder-result-rewards">
                    <div className="mono-caption purple-text mb-0.5 flex items-center gap-1 sm:justify-end">
                      <Sparkles className="w-3 h-3" /> With max 5% Apex Rewards
                    </div>
                    <div className="font-serif text-xl sm:text-2xl font-bold purple-text leading-none"
                         data-testid="apex-finder-result-rewards-price">
                      {format(result.recommendation.price_gbp * 0.95)}
                    </div>
                    <div className="text-[10px] text-[var(--av-text-muted)] mt-1">
                      Save {format(result.recommendation.price_gbp * 0.05)} at checkout
                    </div>
                  </div>
                ) : (
                  <div className="text-[10px] text-[var(--av-text-muted)] sm:text-right max-w-[180px]">
                    Apex Rewards cannot be redeemed on this offer
                  </div>
                )}
              </div>
              {result.recommendation.label && (
                <div className="text-xs text-[var(--av-text-dim)] mb-2 flex items-center gap-1 flex-wrap">
                  <Sparkles className="w-3 h-3 text-[var(--av-purple)]" />
                  {result.recommendation.label}
                </div>
              )}
              {result.recommendation.note && (
                <div className="text-xs purple-text mb-3">
                  {result.recommendation.note}
                </div>
              )}
              <p className="text-sm text-[var(--av-text-dim)] mb-4">
                We've found the best match for the amount you're looking for.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  data-testid="apex-finder-go"
                  onClick={takeMeThere}
                  disabled={going || salesPaused}
                  className="btn-gold w-full sm:flex-1 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {salesPaused ? "Sales Paused" : (going ? "OPENING…" : (<>TAKE ME THERE <ArrowRight className="w-4 h-4 inline ml-1" /></>))}
                </button>
                <button
                  data-testid="apex-finder-reset"
                  onClick={reset}
                  className="btn-outline-gold text-xs sm:text-sm sm:w-auto whitespace-nowrap"
                >
                  <RotateCcw className="w-3 h-3 inline mr-1" /> Try another amount
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
